/**
 * Utility functions to transform API lottery data to component-compatible formats
 */

/**
 * Transform API lottery games to the format expected by the List component
 * @param {Array} apiGames - Array of games from API
 * @returns {Array} - Formatted games array
 */
export const transformLotteryGamesForList = (apiGames = []) => {
  return apiGames
    .filter(game => game.status === "active")
    .map(game => {
      // Create a slug from the game name
      const slug = game.name
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^\w-]+/g, "")
        .replace(/--+/g, "-")
        .replace(/^-+/, "")
        .replace(/-+$/, "");

      return {
        id: game.id,
        stringId: slug,
        provider: game.provider?.name || "Jogacompix",
        name: game.name,
        slug: slug,
        icon: game.logo || `https://via.placeholder.com/150?text=${encodeURIComponent(game.name)}`,
        isFav: false,
        enable: game.status === "active",
        gameType: game.game_type,
        minBet: game.min_bet ? game.min_bet / 100 : null, // Convert cents to dollars
        maxBet: game.max_bet ? game.max_bet / 100 : null, // Convert cents to dollars
        payoutMultiplier: game.default_payout_multiplier,
        draws: game.draws || [],
        figures: game.figures || [],
      };
    });
};

/**
 * Helper to remove accents from string
 */
const removeAccents = str => {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

/**
 * Transform API game figures to animal data format
 * @param {Array} figures - Array of figures from API
 * @returns {Array} - Formatted animal data
 */
export const transformFiguresToAnimalData = (figures = []) => {
  const filtered = figures.filter(figure => figure.status === "active");

  const sorted = filtered.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  return sorted.map(figure => {
    // Use the actual number from the API, not display_order
    const actualNumber = figure.number; // This could be "00", "0", "1", "2", etc.
    const numericNumber = parseInt(actualNumber, 10) || 0; // For color calculation

    // Determine color based on number
    // Special cases: 0 = green, 75 = red (77-board), 99 = red (101-board), 00 = green
    let color;
    if (actualNumber === "00" || actualNumber === 0 || actualNumber === "0") {
      color = "green";
    } else if (actualNumber === 75 || actualNumber === "75" || actualNumber === 99 || actualNumber === "99") {
      color = "red";
    } else {
      // Regular animals: odd = red, even = dark
      color = numericNumber % 2 === 1 ? "red" : "dark";
    }

    const rawName = figure.figure?.name || figure.name || `animal-${actualNumber}`;
    const normalizedName = removeAccents(rawName.toLowerCase());

    const transformed = {
      number: actualNumber, // Use the actual number (00, 0, 1, 2, etc.)
      displayOrder: figure.display_order, // Keep display order for positioning
      name: normalizedName,
      color: color,
      displayName: figure.figure?.name || figure.name || `Animal ${actualNumber}`,
      imageUrl: figure.image_url, // Use the API image URL
      maxBet: figure.max_bet ? figure.max_bet / 100 : null, // Convert cents to dollars
      payoutMultiplier: figure.payout_multiplier,
    };
    return transformed;
  });
};

/**
 * Helper function to format time to HH:MM format (without seconds)
 * @param {string} timeString - Time string from API
 * @returns {string} - Formatted time string
 */
const formatTimeToHourMinute = timeString => {
  if (!timeString) return "";

  // If it's already in HH:MM format, return as is
  if (/^\d{2}:\d{2}$/.test(timeString)) {
    return timeString;
  }

  // If it contains seconds (HH:MM:SS), remove seconds
  if (/^\d{2}:\d{2}:\d{2}$/.test(timeString)) {
    return timeString.substring(0, 5);
  }

  // If it's a full datetime string, extract just the time part
  try {
    const date = new Date(timeString);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  } catch {}

  return timeString; // Return original if can't parse
};

/**
 * Transform API game draws to sorteos format
 * @param {Array} draws - Array of draws from API
 * @returns {Array} - Formatted sorteos array
 */
export const transformDrawsToSorteos = (draws = []) => {
  const filtered = draws.filter(draw => draw.status === "active");

  const transformed = filtered.map(draw => {
    const formattedTime = formatTimeToHourMinute(draw.draw_time);
    const sorteo = {
      id: draw.id,
      hora: formattedTime,
      nombre: draw.draw_name,
      tipo: draw.draw_type,
      closeTime: draw.close_time,
      daysOfWeek: draw.days_of_week,
    };
    return sorteo;
  });

  return transformed;
};

/**
 * Find a game configuration by string ID (slug)
 * @param {Array} apiGames - Array of games from API
 * @param {string} gameStringId - The string ID/slug to search for
 * @returns {Object|null} - Found game or null
 */
export const findGameByStringId = (apiGames = [], gameStringId) => {
  // First try to find by exact name match (case-insensitive)
  const exactMatch = apiGames.find(
    game =>
      game.name.toLowerCase().replace(/\s+/g, "-") === gameStringId ||
      game.name.toLowerCase() === gameStringId.replace(/-/g, " ")
  );

  if (exactMatch) {
    return exactMatch;
  }

  // Fallback: try partial matching
  const partialMatch = apiGames.find(
    game =>
      game.name.toLowerCase().includes(gameStringId.replace(/-/g, " ")) ||
      gameStringId.includes(game.name.toLowerCase().replace(/\s+/g, "-"))
  );

  return partialMatch;
};

/**
 * Get lottery configuration compatible with existing lotteryConfig format
 * @param {Array} apiGames - Array of games from API
 * @returns {Object} - Configuration object keyed by string IDs
 */
export const generateLotteryConfig = (apiGames = []) => {
  const config = {};

  apiGames
    .filter(game => game.status === "active")
    .forEach(game => {
      const slug = game.name
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^\w-]+/g, "")
        .replace(/--+/g, "-")
        .replace(/^-+/, "")
        .replace(/-+$/, "");

      // Determine board type based on game type and number of figures
      let boardType = "default";
      switch (game.game_type) {
        case "Animalitos":
        case "animals":
          {
            const figureCount = game.figures?.length || 0;
            if (figureCount === 101) {
              boardType = "one-oh-one";
            } else if (figureCount === 77) {
              boardType = "seventy-seven";
            } else if (figureCount === 57) {
              boardType = "fifty-seven";
            } else if (figureCount === 40) {
              boardType = "forty";
            } else {
              boardType = "thirty-eight";
            }
          }
          break;
        case "Triple":
          boardType = "triple";
          break;
        case "Terminal":
          boardType = "terminal";
          break;
        default:
          boardType = "default";
          break;
      }

      config[slug] = {
        id: game.id,
        boardType: boardType,
        apiGame: game, // Store reference to original API data
      };
    });

  return config;
};

/**
 * Transform fichas data from API figures
 * @param {Array} figures - Array of figures from API
 * @returns {Array} - Formatted fichas array compatible with existing components
 */
export const transformFiguresToFichas = (figures = []) => {
  return figures
    .filter(figure => figure.status === "active")
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    .map(figure => ({
      id: figure.id,
      numero: figure.number,
      nombre: figure.figure?.name || figure.name,
      img_f: figure.image_url,
      maxBet: figure.max_bet ? figure.max_bet / 100 : null, // Convert cents to dollars
      payoutMultiplier: figure.payout_multiplier,
    }));
};
